import { useEffect, useState, type ReactNode } from 'react';

/**
 * Column settings for report tables: which columns are shown, their widths and the sort.
 * Choices are remembered in this browser.
 */

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  id: string;
  dir: SortDirection;
}

export interface ReportColumn<Row> {
  id: string;
  label: string;
  /** Default width in px. */
  width: number;
  align?: 'left' | 'right';
  /** Off until switched on in the gear menu. */
  hiddenByDefault?: boolean;
  /** What to sort by; columns without one can't be sorted. */
  sortValue?: (row: Row) => string | number;
  /** Direction of the first click (amounts and durations start with the biggest). */
  firstSort?: SortDirection;
  /** On screen; the plain text when left out. */
  cell?: (row: Row) => ReactNode;
  /** Plain text, used for the PDF. */
  text: (row: Row) => string;
  /** CSV headings and values; defaults to the label and text. */
  csv?: { headers: string[]; values: (row: Row) => Array<string | number> };
}

/** A user's choices for one table: kept in this browser, and copied into scheduled reports. */
export interface StoredSettings {
  visible?: Record<string, boolean>;
  widths?: Record<string, number>;
  sort?: SortState;
}

export const MIN_COLUMN_WIDTH = 56;

function readSettings(key: string): StoredSettings {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSettings(key: string, settings: StoredSettings) {
  try {
    if (Object.keys(settings).length) localStorage.setItem(key, JSON.stringify(settings));
    else localStorage.removeItem(key);
  } catch {
    // Storage blocked: settings just last for this visit
  }
}

function compareValues(a: string | number, b: string | number) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function sortRows<Row>(rows: Row[], column: ReportColumn<Row> | undefined, dir: SortDirection) {
  const sortValue = column?.sortValue;
  if (!sortValue) return rows;
  const sign = dir === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, value: sortValue(row) }))
    .sort((a, b) => compareValues(a.value, b.value) * sign || a.index - b.index)
    .map(({ row }) => row);
}

/** Columns with a user's choices applied: what to show, how wide and how to sort. */
export interface ResolvedColumns<Row> {
  /** Every column that applies here, in display order. */
  columns: ReportColumn<Row>[];
  /** The columns switched on. */
  visible: ReportColumn<Row>[];
  isVisible: (column: ReportColumn<Row>) => boolean;
  widthOf: (column: ReportColumn<Row>) => number;
  sort: SortState;
  sortColumn: ReportColumn<Row> | undefined;
}

export function resolveColumns<Row>(
  columns: ReportColumn<Row>[],
  settings: StoredSettings,
  defaultSort: SortState
): ResolvedColumns<Row> {
  const isVisible = (column: ReportColumn<Row>) => settings.visible?.[column.id] ?? !column.hiddenByDefault;
  const shown = columns.filter(isVisible);
  const stored = settings.sort;
  const sort = stored && columns.some((c) => c.id === stored.id && c.sortValue) ? stored : defaultSort;
  return {
    columns,
    visible: shown.length ? shown : columns.slice(0, 1),
    isVisible,
    widthOf: (column) => Math.max(MIN_COLUMN_WIDTH, settings.widths?.[column.id] ?? column.width),
    sort,
    sortColumn: columns.find((c) => c.id === sort.id),
  };
}

export interface ColumnSettings<Row> extends ResolvedColumns<Row> {
  /** What is saved for this browser, e.g. to copy into a scheduled report. */
  stored: StoredSettings;
  /** The user has dragged this column to a width of their own. */
  isResized: (column: ReportColumn<Row>) => boolean;
  toggleColumn: (id: string) => void;
  setWidth: (id: string, width: number) => void;
  resetWidth: (id: string) => void;
  toggleSort: (id: string) => void;
  reset: () => void;
  customised: boolean;
}

export function useColumnSettings<Row>(
  storageKey: string,
  columns: ReportColumn<Row>[],
  defaultSort: SortState
): ColumnSettings<Row> {
  const [settings, setSettings] = useState<StoredSettings>(() => readSettings(storageKey));

  useEffect(() => writeSettings(storageKey, settings), [storageKey, settings]);

  const resolved = resolveColumns(columns, settings, defaultSort);
  const { isVisible, visible, sort } = resolved;

  return {
    ...resolved,
    stored: settings,
    isResized: (column) => settings.widths?.[column.id] !== undefined,
    toggleColumn: (id) => {
      const column = columns.find((c) => c.id === id);
      if (!column) return;
      // Keep at least one column on
      if (isVisible(column) && visible.length <= 1) return;
      setSettings((s) => ({ ...s, visible: { ...s.visible, [id]: !isVisible(column) } }));
    },
    setWidth: (id, width) =>
      setSettings((s) => ({ ...s, widths: { ...s.widths, [id]: Math.round(Math.max(MIN_COLUMN_WIDTH, width)) } })),
    resetWidth: (id) =>
      setSettings((s) => {
        const widths = { ...s.widths };
        delete widths[id];
        return { ...s, widths };
      }),
    toggleSort: (id) => {
      const column = columns.find((c) => c.id === id);
      if (!column?.sortValue) return;
      const dir: SortDirection =
        sort.id === id ? (sort.dir === 'asc' ? 'desc' : 'asc') : (column.firstSort ?? 'asc');
      setSettings((s) => ({ ...s, sort: { id, dir } }));
    },
    reset: () => setSettings({}),
    customised: Boolean(
      Object.keys(settings.visible ?? {}).length || Object.keys(settings.widths ?? {}).length || settings.sort
    ),
  };
}

/** "sorted by Cost, highest first" */
export function describeSort<Row>(table: Pick<ResolvedColumns<Row>, 'sort' | 'sortColumn'>) {
  const column = table.sortColumn;
  if (!column) return '';
  const numeric = column.align === 'right';
  const order = numeric
    ? table.sort.dir === 'asc' ? 'lowest first' : 'highest first'
    : column.id === 'time'
      ? table.sort.dir === 'asc' ? 'oldest first' : 'newest first'
      : table.sort.dir === 'asc' ? 'A to Z' : 'Z to A';
  return `sorted by ${column.label.toLowerCase()}, ${order}`;
}
